import { useEffect, useRef, useState } from 'react';
import { RMk, RMkAt } from '../lib/format';

/** Shared by every JS-driven animation in the app — CSS handles its own via the
 *  reduced-motion block in design.css, but a rAF loop has to ask. */
export const reducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Count a figure up on first paint, and again whenever it changes.
 *
 * Returns the in-flight value. It re-counts from wherever it currently is rather
 * than snapping back to zero, so a hero figure that changes because the user
 * picked a different period travels from the old number to the new one — which
 * is the more useful animation, since the delta is the thing they just asked for.
 */
export default function useCountUp(target, duration = 900) {
  const value = Number(target) || 0;
  const [shown, setShown] = useState(() => (reducedMotion() ? value : 0));
  const from = useRef(0);

  useEffect(() => {
    if (reducedMotion()) { setShown(value); return undefined; }
    const start = from.current;
    const t0 = performance.now();
    const ease = (x) => 1 - (1 - x) ** 3;
    let raf;
    const step = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      if (p < 1) {
        setShown(start + (value - start) * ease(p));
        raf = requestAnimationFrame(step);
      } else {
        // Assign the target, don't compute it. `start + (value - start)` is only
        // bit-exact when start is 0, and callers distinguish "still counting"
        // from "settled" by comparing against the target — a last frame landing
        // a float's breadth away would leave the figure formatted as though it
        // were forever in flight.
        setShown(value);
        from.current = value;
      }
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);

  return shown;
}

/**
 * The whole pattern in one call: `<span>{useCountingRM(gci)}</span>`.
 *
 * In flight the unit is pinned to the destination (RMkAt) so neither the digits
 * nor the suffix change width on the way up; at rest it hands back plain RMk, so
 * the figure is formatted identically to the same number anywhere else on screen.
 */
export function useCountingRM(target, duration = 900) {
  const value = Number(target) || 0;
  const shown = useCountUp(value, duration);
  return shown === value ? RMk(value) : RMkAt(shown, value);
}
